import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { isRouteErrorResponse, Link, type MetaFunction, useLoaderData, useRouteError } from "@remix-run/react";
import { readItems } from "@directus/sdk";
import directus, { type Event } from "~/server/directus.server";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VerificationModal from "~/components/VerificationModal";

import EventContent from "~/components/EventContent";
import EventMetaWidget from "~/components/EventMetaWidget";
import { slugify } from "~/components/utils/helpers";
import { generateEventSchema } from "~/schemas";
import i18n from "~/i18next.server";
import TableOfContents from "~/components/TableOfContents";
import Upvote from "~/components/Upvote";
import { SameYearEvents } from "~/components/SameYearEvents";
import { formatDate } from "~/utils/dates";

export async function loader({ request, params }: LoaderFunctionArgs) {
    const { slug } = params;
    const locale = await i18n.getLocale(request);

    const events = await directus.request(
        readItems("events", {
            fields: [
                "id",
                "status",
                "year",
                "month",
                "day",
                "isRecurring",
                "period",
                "date_created",
                "date_updated",
                { translations: ["*", "audio.filename_disk"], image: ["filename_disk"] }
            ],
            filter: {
                translations: {
                    slug: { _eq: slug },
                    languages_code: { _eq: locale },
                },
            },
            deep: {
                translations: {
                    _filter: {
                        _and: [
                            {
                                languages_code: { _eq: locale },
                                slug: { _eq: slug },
                            },
                        ],
                    },
                },
            },
        })
    );

    if (!events.length) {
        throw new Response("Event not found", { status: 404 });
    }

    const event = events[0] as Event;
    const translation = event.translations?.[0] ?? {
        title: 'Not found',
        header: 'Not found',
        tags: [],
    };

    if (!event) {
        throw new Response("Event not found", { status: 404 });
    }

    return json({
        event,
        meta: {
            title: `${translation.title} - ${formatDate({ year: event.year, month: event.month, day: event.day }, locale)}`,
            description: translation.header,
            keywords: translation.tags.join(', ')
        },
        locale,
        cdnUrl: process.env.CDN_URL as string,
        schema: generateEventSchema(event, locale, {
            hostUrl: process.env.HOST_URL as string,
            cdnUrl: process.env.CDN_URL as string,
            websiteName: process.env.WEBSITE_NAME as string,
            isRecurring: event.isRecurring,
        })
    });
}

export function ErrorBoundary() {
    const error = useRouteError();
    const { t } = useTranslation();

    if (isRouteErrorResponse(error) && error.status === 404) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h1 className="text-4xl font-bold mb-4">{t('404.title')}</h1>
                <p className="text-lg mb-8">{t('404.message')}</p>
                <a href="/" className="text-blue-600 hover:text-blue-800 underline">
                    {t('backToHome')}
                </a>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="text-4xl font-bold mb-4">{t('common.errorTitle')}</h1>
            <p className="text-lg mb-8">{t('common.errorMessage')}</p>
        </div>
    );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    if (!data) return [];

    return [
        { title: data.meta.title },
        {
            name: "description",
            content: data.meta.description
        },
        {
            name: "keywords",
            content: data.meta.keywords
        },
        // Open Graph tags
        {
            property: "og:title",
            content: data.meta.title
        },
        {
            property: "og:description",
            content: data.meta.description
        },
        {
            property: "og:type",
            content: "article"
        },
        {
            property: "og:url",
            content: `https://cokiedy.pl/years/${data.event.year}/events/${data.event.translations[0].slug}`
        },
        // OG Image tags
        {
            property: "og:image",
            content: data.event.image ?
                `https://cdn.whatwhen.day/${data.event.image.filename_disk}` :
                "https://whatwhen.day/og-default.jpg"
        },
        {
            property: "og:image:width",
            content: "1200"
        },
        {
            property: "og:image:height",
            content: "630"
        },
        {
            property: "og:image:alt",
            content: data.meta.title
        },
        // Twitter Card tags
        {
            name: "twitter:card",
            content: "summary_large_image"
        },
        {
            name: "twitter:title",
            content: data.meta.title
        },
        {
            name: "twitter:description",
            content: data.meta.description
        },
        {
            name: "twitter:image",
            content: data.event.image ?
                `https://cdn.whatwhen.day/${data.event.image.filename_disk}` :
                "https://whatwhen.day/og-default.jpg"
        },
        {
            "script:ld+json": data.schema,
        },
    ];
};

export const headers = {
    "Cache-Control": "public, max-age=3600, s-maxage=86400, stale-while-revalidate=604800",
};

export default function EventScreen() {
    const { event, locale, cdnUrl } = useLoaderData<typeof loader>();
    const translation = event.translations[0] ?? {};
    const { t } = useTranslation();
    const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);

    const getStatusBadge = () => {
        switch (translation.status) {
            case 'VERIFIED':
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/10 text-green-500 border border-green-500/20">
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {t('status.VERIFIED')}
                    </div>
                );
            case 'COMMUNITY_VERIFIED':
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-500 border border-blue-500/20">
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                        </svg>
                        {t('status.COMMUNITY_VERIFIED')}
                    </div>
                );
            default:
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-500/10 text-purple-500 border border-purple-500/20">
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M13 7h-2v2h2V7zm0 4h-2v2h2v-2zm-6-4H5v2h2V7zm0 4H5v2h2v-2zM17 5H3c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z" clipRule="evenodd" />
                        </svg>
                        {t('status.AI_GENERATED')}
                    </div>
                );
        }
    };

    return (
        <>
            <div className="flex flex-col md:flex-row gap-8 container mx-auto px-4 py-8 max-w-6xl">
                <div className="md:hidden mb-1">
                    {getStatusBadge()}
                    {translation.audio?.filename_disk && (
                        <div className="mb-4 mt-4">
                            <audio
                                controls
                                className="w-full"
                                src={`${cdnUrl}/${translation.audio.filename_disk}`}
                                aria-label={t('audio.summary')}
                            >
                                <track
                                    kind="captions"
                                    src=""
                                    srcLang={locale}
                                    label={t('audio.captions')}
                                />
                                {t('audio.notSupported')}
                            </audio>
                            <p className="text-xs text-slate-400 mt-1 text-right">
                                {t('audio.aiGeneratedVoice')}
                            </p>
                        </div>
                    )}
                    {translation.content && (
                        <div className="hidden md:block">
                            <TableOfContents content={translation.content} />
                        </div>
                    )}
                </div>

                <div className="flex-1 flex flex-col gap-8">
                    <EventContent
                        title={translation.header || ''}
                        content={translation.content || ''}
                        tags={translation.tags || []}
                        faq={translation.faq || []}
                        image={event.image}
                        cdnUrl={cdnUrl}
                    />
                    <SameYearEvents
                        year={event.year}
                        currentEventId={event.id}
                        locale={locale}
                    />
                </div>

                <div className="md:w-[400px] relative">
                    <div className="md:sticky md:top-4 md:max-h-[calc(100vh-2rem)] md:overflow-y-auto no-scrollbar">
                        <div className="space-y-6 p-4">
                            {translation.audio?.filename_disk && (
                                <div className="mb-4 hidden md:block">
                                    <audio
                                        controls
                                        className="w-full"
                                        src={`${cdnUrl}/${translation.audio.filename_disk}`}
                                        aria-label={t('audio.summary')}
                                    >
                                        <track
                                            kind="captions"
                                            src=""
                                            srcLang={locale}
                                            label={t('audio.captions')}
                                        />
                                        {t('audio.notSupported')}
                                    </audio>
                                    <p className="text-xs text-slate-400 mt-1 text-right">
                                        {t('audio.aiGeneratedVoice')}
                                    </p>
                                </div>
                            )}
                            <div className="flex items-center md:justify-between justify-center">
                                <div className="hidden md:block mb-1">
                                    {getStatusBadge()}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsVerificationModalOpen(true)}
                                    className="inline-flex items-center px-4 py-2 rounded-md bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4 mr-2"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        aria-hidden="true"
                                        role="img"
                                    >
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    {t('verification.verify')}
                                </button>
                            </div>

                            <div>
                                <Upvote
                                    translationId={translation.id}
                                    initialUpvotes={translation.upvotes || 0}
                                />
                            </div>

                            <div>
                                <EventMetaWidget
                                    date={{
                                        year: event.year,
                                        month: event.month,
                                        day: event.day,
                                    }}
                                    meta={translation.meta || {}}
                                    tags={translation.tags || []}
                                />
                            </div>

                            {translation.content && (
                                <div className="hidden md:block">
                                    <TableOfContents content={translation.content} />
                                </div>
                            )}

                            {translation.tags && translation.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {translation.tags.map(tag => (
                                        <Link
                                            to={`/tags/${slugify(tag)}`}
                                            key={tag}
                                            className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full text-sm font-medium text-purple-600 hover:bg-purple-500/15 transition-colors duration-150"
                                        >
                                            {tag}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <VerificationModal
                    isOpen={isVerificationModalOpen}
                    onClose={() => setIsVerificationModalOpen(false)}
                    translationId={translation.id}
                    title={translation.header}
                />
            </div>
        </>
    );
}
